// StripePaymentForm.jsx - FIXED VERSION

import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
    CardElement,
    Elements,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js';
import { Button, Spinner, Alert } from 'reactstrap';
import axiosInstance from '../apiHandles/axiosInstance';
import { useLanguage } from 'context/LanguageProvider';
import { usePremium } from 'context/PremiumProvider';


const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = ({ selectedPlan, onSuccess }) => {
    const { selLanguage } = useLanguage();
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const { isPremium } = usePremium();


    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setProcessing(true);
        setError(null);

        try {
            const cardElement = elements.getElement(CardElement);

            // ✅ ΒΗΜΑ 1: Δημιουργία payment method
            const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
            });

            if (pmError) {
                setError(pmError.message);
                setProcessing(false);
                return;
            }

            console.log('✅ Payment method created:', paymentMethod.id);

            // ✅ ΒΗΜΑ 2: Στείλε στο backend
            const response = await axiosInstance.post('/user/create_subscription/', {
                planId: selectedPlan.id,
                paymentMethodId: paymentMethod.id,
            });

            console.log('✅ Backend response:', response.data);

            if (!response.data.success) {
                setError('Σφάλμα κατά τη δημιουργία συνδρομής');
                setProcessing(false);
                return;
            }

            const { client_secret, status } = response.data;

            // ✅ ΒΗΜΑ 3: Αν υπάρχει client_secret, κάνε confirm (3D Secure)
            if (client_secret) {
                console.log('🔒 Confirming payment with 3D Secure...');

                const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
                    client_secret,
                    {
                        payment_method: paymentMethod.id,
                    }
                );

                if (confirmError) {
                    console.error('❌ 3D Secure failed:', confirmError);
                    setError(confirmError.message);
                    setProcessing(false);
                    return;
                }

                console.log('✅ Payment confirmed:', paymentIntent.status);

                // Έλεγχος αν η πληρωμή ολοκληρώθηκε
                if (paymentIntent.status !== 'succeeded' && paymentIntent.status !== 'processing') {
                    setError('Η πληρωμή δεν ολοκληρώθηκε. Δοκιμάστε ξανά.');
                    setProcessing(false);
                    return;
                }
            }

            // ✅ ΒΗΜΑ 4: Αν το subscription είναι ήδη active, κλείσε το modal
            if (status === 'active' || status === 'trialing') {
                console.log('✅ Subscription is already active!');
                onSuccess();
                window.location.reload();
                return;
            }

            // ✅ ΒΗΜΑ 5: Περίμενε το webhook (polling με timeout)
            console.log('⏳ Waiting for webhook to update premium status...');

            const maxAttempts = 10;
            const delayMs = 1000;

            for (let i = 0; i < maxAttempts; i++) {
                await new Promise(resolve => setTimeout(resolve, delayMs));

                try {

                    console.log(`Attempt ${i + 1}/${maxAttempts}: isPremium = ${isPremium}`);

                    if (isPremium) {
                        console.log('✅ User is now premium!');
                        onSuccess();
                        window.location.reload();
                        return;
                    }
                } catch (err) {
                    console.error('Error checking premium status:', err);
                }
            }

            // ✅ Αν μετά από 10 δευτερόλεπτα δεν έχει γίνει premium
            console.warn('⚠️ Premium status not updated yet, but payment was successful');
            setError(
                selLanguage ? 'Η πληρωμή ήταν επιτυχής! Αν δεν δείτε τα premium χαρακτηριστικά αμέσως, ' +
                    'περιμένετε λίγα δευτερόλεπτα και ανανεώστε τη σελίδα.' : 'Payment was successful! If you don\'t see premium features immediately, ' +
                'please wait a few seconds and refresh the page.'
            );

            // Κλείσε το modal ούτως ή άλλως
            setTimeout(() => {
                onSuccess();
                window.location.reload();
            }, 3000);

        } catch (err) {
            console.error('❌ Payment error:', err);
            const errorMessage = err.response?.data?.error ||
                err.message ||
                'Σφάλμα κατά την επεξεργασία της πληρωμής';
            setError(errorMessage);
        } finally {
            setProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="mb-4">
                <CardElement
                    options={{
                        style: {
                            base: {
                                fontSize: '16px',
                                color: '#424770',
                                '::placeholder': {
                                    color: '#aab7c4',
                                },
                            },
                            invalid: {
                                color: '#9e2146',
                            },
                        },
                    }}
                />
            </div>

            {error && (
                <Alert color="danger" className="mb-3">
                    {error}
                </Alert>
            )}

            <Button
                type="submit"
                color="primary"
                block
                disabled={!stripe || processing}
                size="lg"
            >
                {processing ? (
                    <>
                        <Spinner size="sm" className="mr-2" />
                        Επεξεργασία...
                    </>
                ) : (
                    selLanguage ? `Πληρωμή €${selectedPlan.price}` : `Pay €${selectedPlan.price}`
                )}
            </Button>
        </form>
    );
};

const StripePaymentForm = ({ selectedPlan, onSuccess }) => {
    return (
        <Elements stripe={stripePromise}>
            <CheckoutForm selectedPlan={selectedPlan} onSuccess={onSuccess} />
        </Elements>
    );
};

export default StripePaymentForm;